import { useMemo, useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Instagram, X } from "lucide-react";
import { PORTFOLIO } from "@/lib/assets";

const FILTER_KEYS = ["all", "blackwork", "fineLine", "fullColor", "traditional", "neoTraditional", "tribal", "geometric", "realism", "bioOrganic", "mayan", "piercings"];

export const Portfolio = ({ t }) => {
  const [filter, setFilter] = useState("all");
  const [openIdx, setOpenIdx] = useState(null);
  const [carouselIdx, setCarouselIdx] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(window.innerWidth < 640 ? 2 : 3);

  useEffect(() => {
    const handleResize = () => {
      setItemsPerView(window.innerWidth < 640 ? 2 : 3);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const items = useMemo(
    () => (filter === "all" ? PORTFOLIO : PORTFOLIO.filter((p) => p.cat === filter)),
    [filter]
  );

  const close = () => setOpenIdx(null);
  const prev = () => setOpenIdx((i) => (i === null ? null : (i - 1 + items.length) % items.length));
  const next = () => setOpenIdx((i) => (i === null ? null : (i + 1) % items.length));

  // Lightweight parallax: shift images based on scroll position
  const carouselRef = useRef(null);
  const [parallaxY, setParallaxY] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (!carouselRef.current) return;
      const rect = carouselRef.current.getBoundingClientRect();
      const viewH = window.innerHeight;
      // Only compute when carousel is near viewport
      if (rect.bottom > 0 && rect.top < viewH) {
        const center = rect.top + rect.height / 2;
        const offset = ((center - viewH / 2) / viewH) * -18; // max ±18px
        setParallaxY(offset);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const maxIdx = Math.max(0, items.length - itemsPerView);
  const carouselPrev = () => setCarouselIdx((i) => (i === 0 ? maxIdx : i - 1));
  const carouselNext = () => setCarouselIdx((i) => (i >= maxIdx ? 0 : i + 1));

  // Close lightbox when filter changes & reset carousel
  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setCarouselIdx(0);
    close();
  };

  const getInstagramUrl = () => {
    if (typeof window === "undefined") return "https://www.instagram.com/benditotattoo.pdc/";
    const isMobile = /iPhone|iPad|iPod|Android/i.test(window.navigator.userAgent);
    return isMobile ? "instagram://user?username=benditotattoo.pdc" : "https://www.instagram.com/benditotattoo.pdc/";
  };

  const handleInstagramClick = (e) => {
    if (typeof window === "undefined") return;
    const isMobile = /iPhone|iPad|iPod|Android/i.test(window.navigator.userAgent);
    if (isMobile) {
      e.preventDefault();
      window.location.href = "instagram://user?username=benditotattoo.pdc";
      const fallbackTimer = setTimeout(() => {
        window.location.href = "https://www.instagram.com/benditotattoo.pdc/";
      }, 500);
      return () => clearTimeout(fallbackTimer);
    }
  };

  return (
    <section
      id="work"
      className="relative py-14 md:py-20 bg-bendito-bg"
      data-testid="portfolio-section"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10 reveal">
          <div>
            <div className="eyebrow mb-3">{t.portfolio.eyebrow}</div>
            <h2 className="font-heading text-bendito-ink text-4xl md:text-5xl lg:text-6xl uppercase tracking-tight">
              <span className="ink-underline">{t.portfolio.title}</span>
            </h2>
            <p className="text-bendito-muted mt-3 max-w-xl">{t.portfolio.sub}</p>
          </div>
        </div>

        {/* Filter pills */}
        <div
          className="relative mb-8"
          data-testid="portfolio-filters"
        >
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-3 -mx-1">
            {FILTER_KEYS.map((k) => (
              <button
                key={k}
                onClick={() => handleFilterChange(k)}
                data-testid={`filter-${k}`}
                className={`shrink-0 font-mono text-[11px] tracking-[0.22em] uppercase px-4 py-2.5 border transition-colors ${
                  filter === k
                    ? "bg-bendito-orange text-black border-bendito-orange"
                    : "border-bendito-border text-bendito-muted hover:text-bendito-ink hover:border-bendito-ink"
                }`}
              >
                {t.portfolio.filters[k]}
              </button>
            ))}
          </div>
          {/* Fade gradient indicating more filters */}
          <div className="absolute right-0 top-0 bottom-3 w-8 bg-gradient-to-l from-bendito-bg to-transparent pointer-events-none sm:hidden" />
        </div>

        {/* Carousel */}
        <div className="relative group">
          {/* Carousel container */}
          <div ref={carouselRef} className="overflow-hidden bg-bendito-bg rounded-none">
            <div className="flex transition-transform duration-500 ease-out" style={{ transform: `translateX(-${carouselIdx * (100 / itemsPerView)}%)` }}>
              {items.map((p, i) => (
                <div key={p.id} className="w-1/2 sm:w-1/3 flex-shrink-0 px-2" data-testid={`carousel-item-${i}`}>
                  <button
                    onClick={() => setOpenIdx(i)}
                    className="img-zoom touch-lift group relative aspect-square overflow-hidden bg-bendito-surface focus:outline-none focus:ring-2 focus:ring-bendito-orange w-full"
                  >
                    <img
                      src={p.src}
                      alt={p.alt}
                      loading="lazy"
                      className="parallax-img w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      style={{ transform: `translateY(${parallaxY}px)` }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/0 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between text-left opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-bendito-orange">
                        {p.artist}
                      </span>
                      <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-bendito-ink/80">
                        {t.portfolio.filters[p.cat] || p.cat}
                      </span>
                    </div>
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Prev button */}
          <button
            onClick={carouselPrev}
            className="absolute top-1/2 -translate-y-1/2 -left-5 md:-left-8 bg-bendito-orange/90 hover:bg-bendito-orange text-black p-2 transition-colors opacity-0 group-hover:opacity-100 z-10"
            aria-label="Previous"
            data-testid="carousel-prev"
          >
            <ChevronLeft size={20} />
          </button>

          {/* Next button */}
          <button
            onClick={carouselNext}
            className="absolute top-1/2 -translate-y-1/2 -right-5 md:-right-8 bg-bendito-orange/90 hover:bg-bendito-orange text-black p-2 transition-colors opacity-0 group-hover:opacity-100 z-10"
            aria-label="Next"
            data-testid="carousel-next"
          >
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Carousel indicators — hidden on mobile */}
        <div className="hidden sm:flex justify-center gap-1 mt-6">
          {Array.from({ length: Math.max(0, items.length - (itemsPerView - 1)) }, (_, i) => (
            <button
              key={i}
              onClick={() => setCarouselIdx(Math.min(i, maxIdx))}
              className={`w-2 h-2 rounded-full transition-all ${
                i === carouselIdx ? "bg-bendito-orange w-6" : "bg-bendito-border"
              }`}
              aria-label={`Go to item ${i + 1}`}
              data-testid={`carousel-indicator-${i}`}
            />
          ))}
        </div>

        {/* Instagram CTA banner */}
        <div className="mt-10 md:mt-14">
          <a
            href={getInstagramUrl()}
            onClick={handleInstagramClick}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative block overflow-hidden border border-bendito-border hover:border-bendito-orange transition-all duration-500"
            data-testid="portfolio-instagram-tease"
          >
            {/* Background grid of portfolio thumbnails */}
            <div className="grid grid-cols-4 md:grid-cols-8 opacity-30 group-hover:opacity-50 transition-opacity duration-500">
              {items.slice(0, 8).map((p, i) => (
                <div key={i} className="aspect-square overflow-hidden">
                  <img src={p.src} alt="" loading="lazy" className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700" />
                </div>
              ))}
            </div>
            {/* Overlay content */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/70 flex flex-col items-center justify-center text-center p-6">
              <Instagram className="w-10 h-10 text-bendito-orange mb-4 group-hover:scale-110 transition-transform duration-300" strokeWidth={1.4} />
              <span className="font-heading text-bendito-ink text-2xl md:text-3xl tracking-wider">
                {t.portfolio.more}
              </span>
              <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-bendito-muted mt-3">
                @benditotattoo.pdc
              </span>
              <span className="mt-4 inline-flex items-center gap-2 border border-bendito-orange text-bendito-orange font-mono text-[11px] tracking-[0.2em] uppercase px-5 py-2 group-hover:bg-bendito-orange group-hover:text-black transition-colors duration-300">
                <Instagram size={14} strokeWidth={1.5} />
                {t.portfolio.more}
              </span>
            </div>
          </a>
        </div>
      </div>

      {/* Lightbox */}
      <Dialog open={openIdx !== null} onOpenChange={(v) => !v && close()}>
        <DialogContent
          className="max-w-5xl bg-bendito-bg border-bendito-border p-0 rounded-none"
          data-testid="lightbox"
        >
          <DialogTitle className="sr-only">{t.portfolio.lightboxAlt}</DialogTitle>
          {openIdx !== null && items[openIdx] && (
            <div className="relative">
              <img
                src={items[openIdx].src}
                alt={items[openIdx].alt}
                className="w-full max-h-[85vh] object-contain bg-black"
              />
              <button
                onClick={close}
                className="absolute top-3 right-3 bg-black/70 text-bendito-ink p-2 hover:bg-bendito-orange hover:text-black transition-colors"
                aria-label="Close"
                data-testid="lightbox-close"
              >
                <X size={18} />
              </button>
              <button
                onClick={prev}
                className="absolute top-1/2 -translate-y-1/2 left-3 bg-black/70 text-bendito-ink p-2 hover:bg-bendito-orange hover:text-black transition-colors"
                aria-label="Previous"
                data-testid="lightbox-prev"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={next}
                className="absolute top-1/2 -translate-y-1/2 right-3 bg-black/70 text-bendito-ink p-2 hover:bg-bendito-orange hover:text-black transition-colors"
                aria-label="Next"
                data-testid="lightbox-next"
              >
                <ChevronRight size={20} />
              </button>
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/90 to-transparent p-5 flex justify-between items-end">
                <div>
                  <div className="font-heading text-bendito-ink text-xl tracking-wider">
                    {items[openIdx].artist}
                  </div>
                  <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-bendito-orange">
                    {t.portfolio.filters[items[openIdx].cat]}
                  </div>
                </div>
                <div className="font-mono text-[10px] text-bendito-muted">
                  {openIdx + 1} / {items.length}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Portfolio;
